import { ApplicationWithAdminData } from '../types/admin';
import { User } from '../types/auth';
import { canUserRevertStatus } from './revertUtils';

/**
 * Check if user is a superadmin
 */
export const isSuperAdmin = (user: User | null): boolean => {
  return user?.role === 'admin' && user?.userProfile?.affiliation === 'TODAS';
};

/**
 * Admin actions that can be restricted
 */
export type AdminAction =
  | 'markEvaluated'
  | 'manageEvaluators'
  | 'viewEvaluations'
  | 'revert'
  | 'viewEvaluationStatuses'
  | 'assignReview'
  | 'evaluateAdmissibility';

/**
 * Check if user can perform a specific admin action
 */
export const canUserPerformAction = (
  action: AdminAction,
  user: User | null,
  application?: ApplicationWithAdminData
): boolean => {
  if (!user) return false;

  const isSuper = isSuperAdmin(user);

  switch (action) {
    case 'markEvaluated':
    case 'manageEvaluators':
    case 'viewEvaluations':
    case 'viewEvaluationStatuses':
    case 'assignReview':
    case 'evaluateAdmissibility':
      // Only superadmins can perform evaluation-related actions
      return isSuper;

    case 'revert':
      // Use existing revert permission logic
      return application ? canUserRevertStatus(application, user) : false;

    default:
      return false;
  }
};

/**
 * Get list of application statuses that user can view
 */
export const getVisibleStatusesForUser = (user: User | null): string[] => {
  const baseStatuses = [
    'draft',
    'submitted',
    'under_admissibility_review',
    'admissible',
    'not_admissible',
    'selected',
    'not_selected',
  ];

  // Superadmins can see all statuses separated
  if (isSuperAdmin(user)) {
    return [...baseStatuses, 'under_evaluation', 'evaluated'];
  }

  // Non-superadmins see admissible, under_evaluation, and evaluated combined in one table
  // But they can see selected and not_selected tables
  return baseStatuses;
};

/**
 * Filter applications by what user can see
 */
export const filterApplicationsByPermissions = (
  applications: ApplicationWithAdminData[],
  user: User | null
): ApplicationWithAdminData[] => {
  const visibleStatuses = getVisibleStatusesForUser(user);
  return applications.filter(app => visibleStatuses.includes(app.status));
};

/**
 * Check if user can access user management features
 */
export const canAccessUserManagement = (user: User | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can access alerts
 */
export const canAccessAlerts = (user: User | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can download evaluations
 */
export const canDownloadEvaluations = (user: User | null): boolean => {
  return isSuperAdmin(user);
};